/**
 * Gets the original Supabase storage URL without transformations
 * @param url - Image URL (can be optimized or original)
 * @returns Original storage URL
 */
export function getOriginalUrl(url: string | null | undefined): string {
  if (!url) return '/placeholder.svg';
  
  // If it's already a render URL, convert back to original
  if (url.includes('/storage/v1/render/image/public/')) {
    return url.split('?')[0].replace(
      '/storage/v1/render/image/public/',
      '/storage/v1/object/public/'
    );
  }
  
  return url;
}

/**
 * Optimizes Supabase storage images using transformation parameters
 * Falls back to original URL if transformation is unavailable
 * @param url - Original image URL from Supabase storage
 * @param options - Transformation options
 * @returns Optimized image URL with transformation parameters
 */
export function optimizeSupabaseImage(
  url: string | null | undefined,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: 'webp' | 'avif' | 'origin';
  } = {}
): string {
  // Return placeholder if no URL
  if (!url) return '/placeholder.svg';
  
  // Only transform Supabase storage URLs
  if (!url.includes('supabase.co/storage/v1/object/public/')) {
    return url;
  }

  // Default options for optimal performance
  const {
    width,
    height,
    quality = 80,
    format = 'webp'
  } = options;

  // Build transformation URL
  const transformUrl = url.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );

  // Add transformation parameters
  const params = new URLSearchParams();
  if (width) params.append('width', width.toString());
  if (height) params.append('height', height.toString());
  params.append('quality', quality.toString());
  if (format !== 'origin') params.append('format', format);

  return `${transformUrl}?${params.toString()}`;
}

/**
 * Preset sizes for common use cases
 */
export const IMAGE_SIZES = {
  // Category cards - 400x300 aspect ratio
  categoryCard: { width: 400, height: 300 },
  
  // Product cards desktop - 400x300 aspect ratio
  productCard: { width: 400, height: 300 },
  
  // Product cards mobile - 320x400 aspect ratio
  productCardMobile: { width: 320, height: 400 },
  
  // Featured artisan avatar
  artisanAvatar: { width: 64, height: 64 },
  
  // Product detail page
  productDetail: { width: 800, height: 600 },
  
  // Thumbnails
  thumbnail: { width: 100, height: 100 },
} as const;
